/**
 * Super Bowl Squares utilities: year-scoped paths, legacy detection, and index generation.
 * Aligns with Firebase structure: superBowl/year/{YYYY}, circles/{code}/squaresMeta.
 */

/** Legacy circle codes that show the legacy UI instead of the active Squares experience. */
export const LEGACY_SQUARES_CODES = [
  'HdqCc',
  'IxVH9',
  'OiUop',
  'R4jQS',
  'RQGKs',
  'T0HMI',
  'VjlHN'
]

/**
 * Determines the current Super Bowl year (game is in early Feb, same calendar year).
 * @returns {number} e.g. 2026
 */
export function getCurrentSuperBowlYear() {
  return new Date().getFullYear()
}

/**
 * Builds the Firebase path for year-scoped Super Bowl data.
 * @param {number} [year] - Defaults to current Super Bowl year
 * @returns {string} e.g. 'superBowl/year/2026'
 */
export function getSuperBowlYearPath(year) {
  const y = year ?? getCurrentSuperBowlYear()
  return `superBowl/year/${y}`
}

/**
 * Checks if a circle is a legacy Super Bowl Squares game.
 * @param {string} circleCode
 * @returns {boolean}
 */
export function isLegacySquaresCircle(circleCode) {
  return LEGACY_SQUARES_CODES.includes(circleCode)
}

/**
 * Fisher-Yates shuffle (mutates array, returns it).
 * @param {number[]} arr
 * @returns {number[]}
 */
function fisherYatesShuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]]
  }
  return arr
}

/**
 * Generates two independent random permutations of 0-9 for row/column indexes.
 * Ensures homeIndexes and awayIndexes are not identical.
 * @param {{ maxRetries?: number }} [opts] - maxRetries for re-shuffling awayIndexes if it matches home (default 10)
 * @returns {{ homeIndexes: number[], awayIndexes: number[] }}
 */
export function generateRandomIndexes(opts = {}) {
  const maxRetries = opts.maxRetries ?? 10
  const base = Array.from({ length: 10 }, (_, i) => i)

  const homeIndexes = fisherYatesShuffle([...base])

  let awayIndexes = fisherYatesShuffle([...base])
  let retries = 0
  while (
    JSON.stringify(homeIndexes) === JSON.stringify(awayIndexes) &&
    retries < maxRetries
  ) {
    awayIndexes = fisherYatesShuffle([...base])
    retries++
  }
  if (JSON.stringify(homeIndexes) === JSON.stringify(awayIndexes)) {
    awayIndexes = fisherYatesShuffle([...base])
  }

  return { homeIndexes, awayIndexes }
}

/**
 * Normalizes homeIndexes/awayIndexes from Firebase (array or comma-separated string) to number[].
 * @param {number[] | string | null | undefined} value - From squaresMeta or legacy superBowl
 * @returns {number[] | undefined}
 */
export function normalizeIndexes(value) {
  if (value == null) return undefined
  if (Array.isArray(value)) {
    return value.length === 10 ? value.map(Number) : undefined
  }
  if (typeof value === 'string') {
    const parsed = value.split(',').map((i) => parseInt(i, 10))
    return parsed.length === 10 && parsed.every((n) => !Number.isNaN(n))
      ? parsed
      : undefined
  }
  return undefined
}
